import * as React from 'react'
import {
  Alert,
  Button,
  Dropdown,
  Form,
  Input,
  Menu,
  Spin,
  Tabs,
  Tooltip,
  Popover,
  Badge,
  notification,
  message,
} from 'antd'
import {
  cloneDeep,
  debounce,
  get,
  isEmpty,
  isEqual,
  isNil,
  set,
  orderBy,
  toString as str,
  invoke,
  remove,
  pick,
  flow,
} from 'lodash'
import produce from 'immer'
import { withRouter } from 'react-router-dom'
import { createPrintHandler, resetPrintHandler } from 'helpers/print'
import { setStorageItem, getStorageItem } from 'helpers/localStorage'
import {
  showError,
  showClientNotifications,
  resourceNotFound,
  ValidationError,
  ClientNotifications,
  showValidationError,
} from 'helpers/errors'
import { createResetState, DEBOUNCE, encodeState, HARD_SPACE } from 'helpers/utils'
import {
  createFieldFactory,
  createHandleSubmit,
  createLabelFactory,
  createSaveDocumentItems,
  getChangedItems,
  isReadOnly,
  confirmUnsavedChanges,
  validateMultipleEmails,
  areEmails,
} from 'helpers/formViews'
import { stopEvent, Emitter } from 'helpers/events'
import { isValidDate, formatUserTime } from 'helpers/dateTime'
import { t, toLocaleCurrency } from 'helpers/i18n'
import { allowPricing, getUserPermission } from 'helpers/auth'
import {
  recalculateQuantityOrdered,
  createGeneratePurchaseOrderNumbers,
  calculateOrderTotals,
  getReceivedItems,
  BadgeContainer,
  getTagFieldNames,
} from 'helpers/procurement'
import { createSaveFormInstances, createRefreshFormInstances, FormInstancesContext } from 'helpers/formInstances'
import { FORM_INSTANCES_REFRESH, LIST_MODAL_CLEAR_SELECTION, FORM_CHANGE_EVENT } from 'options/events'
import { getFileExtension } from 'helpers/downloadItems'
import requisitionerFields from 'options/requisitionerFields'
import FormButtons from 'elements/FormButtons'
import FormInstances from 'containers/Orders/Forms'
import Select, { Option, getOptionProps } from 'elements/Select'
import Drawer from 'elements/Drawer'
import Modal from 'elements/Modal'
import Checkbox from 'elements/Checkbox'
import Comments from 'elements/Comments'
import Icon from 'elements/Icon'
import { Row, Col } from 'elements/Grid'
import Documents from 'containers/Documents/ChildListView'
import OrderItems from 'containers/Orders/Items'
import Help from 'elements/Help'
import SideForm from 'containers/Orders/FormView/SideForm'
import OrderSuppliers from 'containers/Orders/Suppliers'
import PackingListNumber from './PackingListNumber'
import SendEmail from 'components/SendEmail'
import Print from 'containers/Orders/Print'

const getStorageKey = () => 'orders.formView'

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.printableRef = React.createRef()
    this.handleFormSubmit = createHandleSubmit(this)
    this.saveDocumentItems = createSaveDocumentItems(this, 'Order')
    this.repopulateOrderItems = debounce(this.repopulateOrderItems, DEBOUNCE)
    this.refreshOrderSupplierItems = debounce(this.refreshOrderSupplierItems, DEBOUNCE)
    this.generatePurchaseOrderNumbers = createGeneratePurchaseOrderNumbers(this)
    this.resetState = createResetState(
      this,
      { requireQuantity: true, tabsActiveKey: 'items' },
      getStorageItem(getStorageKey(), {})
    )
    this.saveFormInstances = createSaveFormInstances(this, { objectType: 'Order' })
    this.refreshFormInstances = createRefreshFormInstances(this, { objectType: 'Order' })
  }

  async componentDidMount() {
    await this.resetState()
    await this.fetchItem()

    Emitter.on(FORM_INSTANCES_REFRESH, this.refreshFormInstances)
    Emitter.on(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  componentWillUnmount() {
    Emitter.off(FORM_INSTANCES_REFRESH, this.refreshFormInstances)
    Emitter.off(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  handleFormChange = () => this.setState({ formHasChanged: true })

  beforeValidateFields = () => {
    requisitionerFields.forEach(({ key }) =>
      this.props.form.setFields({ [key]: { value: this.state.item[key], errors: null } })
    )
  }

  promiseState = (state = {}) => new Promise((resolve) => this.setState(state, resolve))

  saveState = () => setStorageItem(getStorageKey(), pick(this.state, ['quickscan', 'requireQuantity']))

  getItemId = () => {
    const itemId = [
      this.state.item?.id,
      this.props.linkTargetRecord?.orderId,
      this.props.linkTargetRecord?.relatedOrderId,
      this.props.linkTargetRecord?.objectId,
    ].find(Boolean)

    if (itemId && this.props.userIsInRoleSupplier) {
      const targetTenantId = this.props.filterDto?.tenantId ?? 0

      return `${itemId}?targetTenantId=${targetTenantId}`
    }

    return itemId
  }

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({})
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType ?? 'order' }),
      ])

      const item = cloneDeep(responses[0].value.data)
      const customerId = item.customerId || this.props.user.customerId
      const targetTenantId = this.props.filterDto?.tenantId ?? 0

      const [documentItems, billToAddressList, shipToAddressList, orderItems, orderSupplierItems, formInstanceItems] =
        await Promise.all([
          item.id
            ? this.props.getDocumentItems({
                request: {
                  domainObjectId: item.id,
                  domainObjectType: 'Order',
                  documentType: 'ObjectDocument',
                },
                targetTenantId,
              })
            : Promise.resolve(null),
          this.props.getAddressList({ type: 'billto', targetTenantId, customerId }),
          this.props.getAddressList({ type: 'shipto', targetTenantId, customerId }),
          item.id
            ? this.props.getOrderItems(item.id, { includeInventoryStatus: true, targetTenantId })
            : Promise.resolve(null),
          item.id ? this.props.getOrderSupplierItems(item.id, { targetTenantId }) : Promise.resolve(null),
          item.id
            ? this.props.getFormInstanceItems({
                objectType: 'Order',
                objectId: item.id,
                targetTenantId,
              })
            : Promise.resolve(null),
        ])

      const populatedOrderItems = get(orderItems, 'value.data.items', []).map((each) => ({
        ...each,
        populated: true,
        timestamp: new Date().toJSON(),
      }))

      this.setState(
        {
          item: cloneDeep(item),
          itemOriginal: cloneDeep(item),
          fieldSettings: responses[1].value.data.fieldSettings,
          documentItems: get(documentItems, 'value.data.items', []),
          documentItemsOriginal: get(documentItems, 'value.data.items', []),
          billToAddressList: get(billToAddressList, 'value.data.items', []),
          shipToAddressList: get(shipToAddressList, 'value.data.items', []),
          orderItems: cloneDeep(populatedOrderItems),
          orderItemsOriginal: cloneDeep(populatedOrderItems),
          orderItemsTotals: get(orderItems, 'value.data.pageTotals', {}),
          orderSupplierItems: get(orderSupplierItems, 'value.data.items', []),
          orderSupplierItemsOriginal: get(orderSupplierItems, 'value.data.items', []),
          orderSupplierItemsTotals: get(orderSupplierItems, 'value.data.pageTotals', {}),
          formInstanceItems: get(formInstanceItems, 'value.data.items', []),
          formInstanceItemsOriginal: get(formInstanceItems, 'value.data.items', []),
          formHasChanged: false,
        },
        this.generatePurchaseOrderNumbers
      )
    } catch (error) {
      showError({ error })
    }
  }

  refetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const response = await this.props.getItem(itemId).catch((error) => {
        this.props.onCancel?.()
        throw error
      })

      const item = cloneDeep(response.value.data)

      this.setState({
        item: cloneDeep(item),
        itemOriginal: cloneDeep(item),
      })
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        if (name === 'shipToAddressId') {
          draft.item.taxRate = draft.shipToAddressList.find((one) => one.id === value).taxRate / 100
        }

        this.props.form.setFieldsValue({ [name]: value })
      }),
      this.refreshOrderTotals
    )

  saveItem = async (item) => {
    if (!item) {
      throw new Error('item is undefined')
    }

    try {
      this.setState({ clientNotifications: null })

      const response = await this.props.saveOrder({
        order: item,
        orderItems: produce(getChangedItems(this.state.orderItemsOriginal, this.state.orderItems), (draft) => {
          draft.creating = draft.creating.map((each) => ({
            ...each,
            quantityReceived: each.quantityReceived + each.quantityReceivedDelta,
          }))

          draft.updating = draft.updating.map((each) => ({
            ...each,
            quantityReceived: each.quantityReceived + each.quantityReceivedDelta,
          }))
        }),
        orderSuppliers: getChangedItems(this.state.orderSupplierItemsOriginal, this.state.orderSupplierItems),
      })

      const { failureCount = 0, clientNotifications = [], items = [] } = response.value.data

      showClientNotifications({ response })

      if (failureCount > 0) {
        this.setState({ clientNotifications })

        throw new Error()
      }

      const data = items[0]

      if (data.id) {
        await this.saveDocumentItems(data.id)
      }

      return { value: { data } }
    } catch (error) {
      const clientNotifications = error?.data?.clientNotifications ?? []

      showClientNotifications({ clientNotifications })

      if (!isEmpty(clientNotifications)) {
        this.setState({ clientNotifications })

        throw new Error()
      } else {
        throw error
      }
    }
  }

  handleSideFormSubmit = async (sideFormState) => {
    const {
      assetDisplayName = '',
      jobDisplayName = '',
      locationDisplayName = '',
      operatorDisplayName = '',
      ...params
    } = cloneDeep(sideFormState)

    try {
      this.setState({ sideFormLoading: true, tabsActiveKey: 'items', formButtonsDisabled: true })

      const quantityOrderedMin = this.props.customer.generalSettings.allowZeroQuantityOnOrderedItems ? 0 : 1

      const response = await this.props.populateOrderItems([
        {
          quantityOrdered: params.quantity ?? quantityOrderedMin,
          barcode: params.barcode,
          assetId: params.asset?.id,
          assetName: params.asset?.name ?? `*${assetDisplayName}`,
          assetNumber: params.asset?.number,
          assetBarcode: params.asset?.barcode ?? params.assetBarcode,
          jobId: params.job?.id,
          jobName: params.job?.name ?? `*${jobDisplayName}`,
          jobNumber: params.job?.number,
          jobBarcode:
            params.job?.barcode ??
            params.jobBarcode ??
            (!this.props.customer.tagSettings.tag1Restricted ? jobDisplayName : undefined),
          locationId: params.location?.id,
          locationName: params.location?.name ?? `*${locationDisplayName}`,
          locationNumber: params.location?.number,
          locationBarcode: params.location?.barcode ?? params.locationBarcode,
          operatorId: params.operator?.id,
          operatorName: params.operator?.name ?? `*${operatorDisplayName}`,
          operatorNumber: params.operator?.number,
          operatorBarcode: params.operator?.barcode ?? params.operatorBarcode,
          tagA: params.tagA,
          tagB: params.tagB,
          tagC: params.tagC,
          tagD: params.tagD,
          tagE: params.tagE,
          userName: this.props.user.userName,
          sourceCatalogName: params.sourceCatalogName,
        },
      ])

      this.setState({ sideFormLoading: false })

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      await this.promiseState(
        produce((draft) => {
          const seed = Date.now() * -1

          draft.orderItems.unshift(
            ...response.value.data.items.map((each, index) => ({
              ...each,
              quantityOrdered: recalculateQuantityOrdered(each, this.props.customer),
              id: seed - index,
              orderItemId: seed - index,
              populated: true,
              timestamp: new Date().toJSON(),
            }))
          )
        })
      )
      await this.refreshOrderTotals()
      await this.refreshOrderSupplierItems()
    } catch (error) {
      showError({ error })

      this.setState({ sideFormLoading: false })

      return false
    } finally {
      this.setState({ formButtonsDisabled: false })
    }

    return true
  }

  repopulateOrderItems = async () => {
    let failureCount = 0

    try {
      const { orderItems } = this.state

      const populatedOrderItems = await Promise.all(
        orderItems.map((each) => {
          const { id, orderItemId, populated } = each

          if (populated) {
            return each
          }

          this.setState({ formButtonsDisabled: true, orderItemsLoading: true })

          return this.props
            .populateOrderItems([
              {
                ...each,
                id: id > 0 ? id : undefined,
                orderItemId: orderItemId > 0 ? orderItemId : undefined,
              },
            ])
            .then((response) => {
              showClientNotifications({ response })

              failureCount += response.value.data.failureCount

              return {
                ...(response.value.data.items[0] ?? each),
                id,
                orderItemId,
                populated: true,
                timestamp: new Date().toJSON(),
              }
            })
        })
      )

      await this.promiseState({ orderItems: populatedOrderItems })
    } catch (error) {
      failureCount++
      showError({ error })
    } finally {
      this.setState({ formButtonsDisabled: false, orderItemsLoading: false })
    }

    return failureCount
  }

  handleOrderItemsChange = async (changed = []) => {
    let failureCount = 0
    let values = changed

    try {
      if (changed.some((one) => one.copyTagValuesToAllItems)) {
        const item = { ...changed.find((one) => one.copyTagValuesToAllItems), copyTagValuesToAllItems: false }
        const tagValues = pick(item, getTagFieldNames())
        const otherItems = changed.filter((each) => each.id !== item.id).map((each) => ({ ...each, ...tagValues }))
        values = [item, ...otherItems]
      }

      const { deleting = [] } = getChangedItems(this.state.orderItems, values)
      const issuedOrderItems = deleting.filter((each) => each.quantityIssued > 0)

      if (!isEmpty(issuedOrderItems)) {
        notification.error({
          message: t('error'),
          description: t('deleteIssuedOrderItemError'),
        })
      }

      await this.promiseState(
        produce((draft) => {
          draft.orderItems = orderBy(
            [...values, ...issuedOrderItems],
            [(each) => draft.orderItems.findIndex((one) => one.id === each.id)]
          )

          const orderItemsSupplierIds = draft.orderItems.map((each) => each.supplierId)

          remove(draft.orderSupplierItems, (each) => !orderItemsSupplierIds.includes(each.supplierId))

          draft.tabsActiveKey = 'items'
        })
      )
      failureCount += await this.repopulateOrderItems()
      failureCount += await this.refreshOrderSupplierItems()
    } catch (error) {
      failureCount++
      showError({ error })
    } finally {
      this.setState({ formButtonsDisabled: false, orderItemsLoading: false })
    }

    if (failureCount > 0) {
      throw new Error()
    }
  }

  handleOrderSuppliersItemsChange = async (values) => {
    let failureCount = 0

    try {
      this.setState({ formButtonsDisabled: true })
      await this.promiseState({ orderSupplierItems: values })
      await this.refreshOrderTotals()
      await this.generatePurchaseOrderNumbers()
    } catch (error) {
      failureCount++
      showError({ error })
    } finally {
      this.setState({ formButtonsDisabled: false })
    }

    if (failureCount > 0) {
      throw new Error()
    }
  }

  refreshOrderTotals = () =>
    this.promiseState(
      produce((draft) => {
        Object.assign(draft.item, calculateOrderTotals({ customer: this.props.customer, ...draft }))
      })
    )

  refreshOrderSupplierItems = async () => {
    let failureCount = 0

    try {
      this.setState({ formButtonsDisabled: true })

      const quantityOrderedMin = this.props.customer.generalSettings.allowZeroQuantityOnOrderedItems ? 0 : 1

      const supplierIds = this.state.orderItems
        .filter((each) => Boolean(each.supplierId) && each.quantityOrdered >= quantityOrderedMin)
        .map((each) => each.supplierId)

      const suppliers = await Promise.all(
        [...new Set(supplierIds)].map((each) => this.props.getSupplier(each).then((r) => r.value.data))
      )

      const seed = Date.now() * -1
      const orderSupplierItems = suppliers.map((each, index) => {
        const existing = this.state.orderSupplierItems.find((one) => one.supplierId === each.id)

        if (existing) {
          return existing
        }

        return {
          id: seed - index,
          orderSupplierId: seed - index,
          adjustments: 0,
          confirmedDescription: 'No',
          freight: 0,
          orderId: this.state.item.id,
          purchaseOrderNumber: '',
          shippingInstructions: each.shippingInstructions,
          supplierId: each.id,
          supplierName: each.name,
        }
      })

      await this.promiseState({ orderSupplierItems })
      await this.refreshOrderTotals()
      await this.generatePurchaseOrderNumbers()
    } catch (error) {
      failureCount++
      showError({ error })
    } finally {
      this.setState({ formButtonsDisabled: false })
    }

    return failureCount
  }

  getSelectedSupplierId = () => {
    const supplierIds = [...new Set(this.state.orderItems.map((each) => each.supplierId))]
    return supplierIds.length === 1 ? supplierIds[0] : this.state.orderItemsFilterDto?.supplierId
  }

  invokeAction = async (action) => {
    try {
      await this.props.onSave?.(true)

      this.setState({ clientNotifications: null })

      const response = await invoke(this.props, action, this.state.item.id)

      const { failureCount = 0, clientNotifications = [] } = response.value.data

      showClientNotifications({ response })

      if (failureCount > 0) {
        this.setState({ clientNotifications })

        throw new Error()
      }

      await this.refetchItem()
    } catch (error) {
      const clientNotifications = error?.data?.clientNotifications ?? []

      showClientNotifications({ clientNotifications })

      if (!isEmpty(clientNotifications)) {
        this.setState({ clientNotifications })
      } else {
        showError({ error })
      }
    } finally {
      await this.props.onSave?.(false)
    }
  }

  handleActionsMenuClick = ({ key }) => {
    switch (key) {
      case 'text/csv':
      case 'text/html':
      case 'application/pdf':
      case 'application/vnd.ms-excel':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          Emitter.emit('orders.orderItems.downloadItems', {
            parentId: this.state.item.id,
            acceptMimeType: key,
            pageTitle: `${t('order')} - ${this.state.item.id} - ${t('itemsTab')}`,
            fileExtension: getFileExtension(key),
          })
        }
        break

      case 'receiveAll':
      case 'unreceiveAll': {
        const supplierId = this.getSelectedSupplierId()

        if (isNil(supplierId)) {
          message.error(t('selectItemsSupplier'))
        } else {
          Modal.confirm({
            autoFocusButton: 'ok',
            title: t(`confirm${key}`),
            content: t(`confirm${key}Description`),
            okText: t('yes'),
            cancelText: t('no'),
            onOk: () =>
              this.setState(
                produce((draft) => {
                  draft.orderItems
                    .filter((each) => each.supplierId === supplierId && !each.lockedForCycleCount)
                    .forEach((one) => {
                      one.quantityReceived = key === 'receiveAll' ? one.quantityOrdered : 0
                    })
                })
              ),
          })
        }
        break
      }

      case 'cancelOrder':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          Modal.confirm({
            autoFocusButton: 'ok',
            title: t('confirmCancelOrder'),
            content: t('confirmCancelOrderDescription'),
            okText: t('yes'),
            okType: 'danger',
            cancelText: t('no'),
            onOk: () => this.invokeAction('cancelOrder'),
          })
        }
        break

      case 'deleteOrder':
        Modal.confirm({
          autoFocusButton: 'ok',
          title: t('confirmDeleteOrder'),
          content: t('confirmDeleteOrderDescription'),
          okText: t('delete'),
          okType: 'danger',
          cancelText: t('cancel'),
          onOk: async () => {
            try {
              const response = await this.props.deleteOrder(this.state.item.id)

              showClientNotifications({ response })

              if (response.value.data.failureCount > 0) {
                throw new Error()
              }

              this.props.onSaveAndClose?.()
            } catch (error) {
              showError({ error })
            }
          },
        })
        break

      case 'sendEmail':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          this.setState({ sendEmailVisible: true })
        }
        break

      case 'orderAuditReport':
        confirmUnsavedChanges(() => {
          this.props.history.push(
            `/reports/audit/orderAuditReport?${encodeState({
              search: str(this.state.item.id),
              searchFields: ['objectId'],
              searchType: 'Contains',
              showFilter: true,
              filterDto: {
                showOrdering: true,
                dateRange: { dateRangeType: 'LastNMonths', lastNValue: 12 },
              },
            })}`
          )
          this.props.onCancel()
        })
        break

      case 'orderDetailReport':
        confirmUnsavedChanges(() => {
          this.props.history.push(
            `/reports/orders/usageOrderReport?${encodeState({
              showFilter: true,
              filterDto: {
                orderId: this.state.item.id,
                showOrdering: true,
                dateRange: { dateRangeType: 'LastNMonths', lastNValue: 12 },
              },
            })}`
          )
          this.props.onCancel()
        })
        break

      case 'receivingAuditReport':
        confirmUnsavedChanges(() => {
          this.props.history.push(
            `/reports/inventory/receivingAuditReport?${encodeState({
              search: str(this.state.item.id),
              searchFields: ['orderId'],
              searchType: 'Contains',
              showFilter: true,
              filterDto: {
                showOrdering: true,
                dateRange: { dateRangeType: 'LastNMonths', lastNValue: 12 },
              },
            })}`
          )
          this.props.onCancel()
        })
        break

      case 'print':
        this.setState(
          produce((draft) => {
            draft.printItem = { ...draft.item, ...this.props.form.getFieldsValue() }
            draft.printVisible = true
          })
        )
        break

      default:
        message.warn(t('underDevelopment'))
        break
    }
  }

  invokeExtraAction = async (action) => {
    const { item } = this.state

    if (this.hasUnsavedChanges()) {
      message.error(t('saveChangesFirst'))
      return
    }

    if (
      action === 'sendOrder' &&
      this.props.customer.generalSettings.requireRequisitionerOnOrder &&
      requisitionerFields.some(({ key }) => isEmpty(item[key]))
    ) {
      this.setState({ validationErrors: true, tabsActiveKey: 'order' }, () => {
        showValidationError()
        requisitionerFields.forEach(({ key }) => {
          if (isEmpty(item[key])) {
            this.props.form.setFields({
              [key]: {
                value: item[key],
                errors: [new Error(t('requiredField'))],
              },
            })
          }
        })
      })
    } else {
      try {
        await this.props.onSave?.(true)

        this.setState({
          [`${action}ButtonLoading`]: true,
          clientNotifications: null,
        })

        if (action === 'resendOrder') {
          const supplierId = this.getSelectedSupplierId()

          if (isNil(supplierId)) {
            message.error(t('selectSupplierToResendOrder'))
          } else {
            const response = await this.props.resendOrder({
              orderId: item.id,
              supplierId,
            })

            showClientNotifications({ response })

            if (response.value.data.failureCount > 0) {
              throw new Error()
            }
          }
        } else if (this.props[action]) {
          const targetTenantId = this.props.filterDto?.tenantId ?? 0

          const response = await invoke(this.props, action, item.id, { targetTenantId })

          showClientNotifications({ response })

          if (response.value.data.failureCount > 0) {
            throw new Error()
          }

          await this.refetchItem()
        } else {
          message.warn(t('underDevelopment'))
        }

        await this.props.onSave?.(false)
      } catch (error) {
        showError({ error })
        await this.props.onSave?.(false)
      } finally {
        this.setState({ [`${action}ButtonLoading`]: false })
      }
    }
  }

  hasUnsavedChanges = () =>
    this.state.formHasChanged ||
    !this.state.item?.id ||
    !isEqual(this.state.item, this.state.itemOriginal) ||
    !isEqual(this.state.orderItems, this.state.orderItemsOriginal) ||
    !isEqual(this.state.orderSupplierItems, this.state.orderSupplierItemsOriginal) ||
    !isEqual(this.state.documentItems, this.state.documentItemsOriginal)

  handleSubmit = (e, { saveAndClose = true } = {}) => {
    stopEvent(e)

    if (this.props.customer.generalSettings.requirePackingListNumber) {
      const receivedItems = getReceivedItems(this.state.orderItemsOriginal, this.state.orderItems)

      if (receivedItems.some((one) => isEmpty(one.packingListNumber))) {
        return this.setState({ showAddPackingListNumberModal: true, saveAndClose })
      }

      return this.handleFormSubmit(e, { saveAndClose })
    }

    return this.handleFormSubmit(e, { saveAndClose })
  }

  handleAddPackingListNumberSubmit = ({ packingListNumber }) =>
    this.setState(
      produce((draft) => {
        const receivedItemIds = getReceivedItems(draft.orderItemsOriginal, draft.orderItems).map((each) => each.id)

        draft.orderItems.forEach((each) => {
          if (receivedItemIds.includes(each.id)) {
            each.packingListNumber = packingListNumber
          }
        })

        draft.showAddPackingListNumberModal = false
      }),
      (e) => this.handleSubmit(e, { saveAndClose: this.state.saveAndClose })
    )

  handleSendEmailSubmit = async (recipients) => {
    try {
      this.setState({ sendEmailLoading: true })

      const response = await this.props.emailOrder({
        recipients,
        order: this.state.item ?? {},
        orderItems: this.state.orderItems ?? [],
        orderSuppliers: this.state.orderSupplierItems ?? [],
      })

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      this.setState({
        sendEmailLoading: false,
        sendEmailVisible: false,
      })
    } catch (error) {
      showError({ error })
      this.setState({ sendEmailLoading: false })
    }
  }

  handleAddFromList = async (sideFormState) => {
    const {
      assetDisplayName = '',
      jobDisplayName = '',
      locationDisplayName = '',
      operatorDisplayName = '',
      ...params
    } = cloneDeep(sideFormState)

    const asset = (params?.assets?.items ?? []).find((one) => one.id === params.assetId)
    const job = (params?.jobs?.items ?? []).find((one) => one.id === params.jobId)
    const location = (params?.locations?.items ?? []).find((one) => one.id === params.locationId)
    const operator = (params?.operators?.items ?? []).find((one) => one.id === params.operatorId)
    const quantityOrderedMin = this.props.customer.generalSettings.allowZeroQuantityOnOrderedItems ? 0 : 1

    this.setState({ addFromListLoading: true, tabsActiveKey: 'items' })

    try {
      const response = await this.props.populateOrderItems(
        params.addFromListItems.map((each) => ({
          quantityOrdered: Math.max(each.quantity, quantityOrderedMin),
          barcode: each.barcode,
          assetId: asset?.id,
          assetName: asset?.name ?? `*${assetDisplayName}`,
          assetBarcode: asset?.barcode,
          jobId: job?.id,
          jobName: job?.name ?? `*${jobDisplayName}`,
          jobNumber: job?.number,
          jobBarcode: job?.barcode ?? jobDisplayName,
          locationId: location?.id,
          locationName: location?.name ?? `*${locationDisplayName}`,
          locationBarcode: location?.barcode,
          operatorId: operator?.id,
          operatorName: operator?.name ?? `*${operatorDisplayName}`,
          operatorNumber: operator?.number,
          operatorBarcode: operator?.barcode,
          tagA: params.tagA,
          tagB: params.tagB,
          tagC: params.tagC,
          tagD: params.tagD,
          tagE: params.tagE,
          userName: this.props.user.userName,
        }))
      )

      this.setState({ sideFormLoading: false })

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      response.value.data.items.map((each) => message.success(`${each.barcode} ${t('addToOrderSuccess')}`))

      await this.promiseState(
        produce((draft) => {
          const seed = Date.now() * -1
          draft.orderItems.unshift(
            ...response.value.data.items.map((each, index) => ({
              ...each,
              quantityOrdered: recalculateQuantityOrdered(each, this.props.customer),
              id: seed - index,
              orderItemId: seed - index,
              populated: true,
              timestamp: new Date().toJSON(),
            }))
          )
        })
      )
      await this.refreshOrderTotals()
      await this.refreshOrderSupplierItems()

      Emitter.emit(LIST_MODAL_CLEAR_SELECTION)
    } catch (error) {
      showError({ error })

      return false
    } finally {
      this.setState({ addFromListLoading: false })
    }

    return true
  }

  handleSubmitOrderClick = async () => {
    const { item } = this.state
    const quantityOrderedMin = this.props.customer.generalSettings.allowZeroQuantityOnOrderedItems ? 0 : 1

    if (
      this.props.customer.generalSettings.requireRequisitionerOnOrder &&
      requisitionerFields.some(({ key }) => isEmpty(item[key])) &&
      this.state.orderItems.some((one) => one.quantityOrdered >= quantityOrderedMin)
    ) {
      this.setState({ showRequisitionerInformationModal: true })
    } else {
      try {
        await this.props.onSave?.(true)

        this.setState({ actionButtonLoading: true })

        const response = await this.props.submitItem(item.id)

        showClientNotifications({ response })

        if (response.value.data.failureCount > 0) {
          throw new Error()
        }

        await this.refetchItem()

        this.setState({ showRequisitionerInformationModal: false })
      } catch (error) {
        showError({ error })
      } finally {
        await this.props.onSave?.(false)
        this.setState({ actionButtonLoading: false })
      }
    }
  }

  handleSaveAndSubmitOrderClick = async (e) => {
    const { item } = this.state

    try {
      await this.props.onSave?.(true)

      this.setState({ actionButtonLoading: true })

      await this.saveItem(item)

      const response = await this.props.submitItem(item.id)

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      await this.refetchItem()

      this.setState({ showRequisitionerInformationModal: false })
    } catch (error) {
      showClientNotifications({ error })
      showError({ error })
    } finally {
      await this.props.onSave?.(false)
      this.setState({ actionButtonLoading: false })
    }
  }

  render() {
    const { item, fieldSettings, orderItems, orderSupplierItems } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const targetTenantId = this.props.filterDto?.tenantId ?? 0
    const extraButtonsDisabled =
      this.state.formButtonsDisabled || this.state.saveButtonLoading || this.state.saveAndCloseButtonLoading
    const userIsSupplierOrDistributer = this.props.userIsInRoleSupplier || this.props.userIsInRoleDistributor
    const orderIsPartiallyReceived = this.state.orderItems.some((one) => one.quantityReceived > 0)

    const orderIsReadOnly =
      isReadOnly(this) || userIsSupplierOrDistributer || ['Draft', 'Cancelled'].includes(item.status)

    const orderIsEditable =
      !orderIsReadOnly &&
      item.status !== 'Processed' &&
      (['New', 'PendingApproval'].includes(item.status) || this.props.customer.generalSettings.allowOrderEditAfterSend)

    const orderIsReceivable =
      !orderIsReadOnly &&
      ['Sent', 'Received', 'Processed'].includes(item.status) &&
      this.state.orderItems.some((one) => one.quantityOrdered > 0)

    const quantityOrderedMin = this.props.customer.generalSettings.allowZeroQuantityOnOrderedItems ? 0 : 1

    return (
      <>
        <Form
          layout="vertical"
          colon={false}
          onSubmit={!orderIsEditable && !orderIsReceivable ? stopEvent : this.handleSubmit}
        >
          {item.id ? (
            <Dropdown
              overlay={
                <Menu onClick={this.handleActionsMenuClick}>
                  {orderIsReceivable &&
                    getUserPermission('ReceiveOrders') !== 'No' && [
                      <Menu.Item key="receiveAll">{t('receiveAll')}</Menu.Item>,
                      <Menu.Item key="unreceiveAll">{t('unreceiveAll')}</Menu.Item>,
                      <Menu.Divider key="divider" />,
                    ]}
                  {getUserPermission('Reports') !== 'No' &&
                    !userIsSupplierOrDistributer && [
                      <Menu.Item key="receivingAuditReport">{t('receivingAuditReport')}</Menu.Item>,
                      <Menu.Item key="orderAuditReport">{t('orderAuditReport')}</Menu.Item>,
                      <Menu.Item key="orderDetailReport">{t('orderDetailReport')}</Menu.Item>,
                      <Menu.Divider key="divider" />,
                    ]}
                  {userIsSupplierOrDistributer && <Menu.Item key="packingSlip">{t('packingSlip')}</Menu.Item>}
                  <Menu.SubMenu key="export" title={t('export')}>
                    <Menu.Item key="text/csv">{t('asCsv')}</Menu.Item>
                    <Menu.Item key="application/vnd.ms-excel">{t('asExcel')}</Menu.Item>
                    <Menu.Item key="application/pdf">{t('asPdf')}</Menu.Item>
                    <Menu.Item key="text/html">{t('asHtml')}</Menu.Item>
                  </Menu.SubMenu>
                  <Menu.Item key="sendEmail">{t('email')}</Menu.Item>
                  {orderIsEditable &&
                    item.status === 'New' && [
                      <Menu.Divider key="divider" />,
                      <Menu.Item key="deleteOrder">{t('deleteOrder')}</Menu.Item>,
                    ]}
                  {orderIsEditable &&
                    item.status === 'Sent' &&
                    !orderIsPartiallyReceived && [
                      <Menu.Divider key="divider" />,
                      <Menu.Item
                        key="cancelOrder"
                        disabled={!this.props.customer.generalSettings.allowOrderEditAfterSend}
                      >
                        {t('cancelOrder')}
                      </Menu.Item>,
                    ]}
                  {orderIsEditable &&
                    item.status === 'PendingApproval' && [
                      <Menu.Divider key="divider" />,
                      <Menu.Item
                        key="cancelOrder"
                        disabled={!this.props.customer.generalSettings.allowOrderEditAfterSend}
                      >
                        {t('cancelOrder')}
                      </Menu.Item>,
                    ]}
                  <Menu.Divider />
                  <Menu.Item key="print" disabled={!item.id}>
                    {t('print...')}
                  </Menu.Item>
                </Menu>
              }
              trigger={['click']}
            >
              <Button className="form-items-actions">
                {t('actions')}
                <Icon type="KeyboardArrowDown" />
              </Button>
            </Dropdown>
          ) : null}
          <div className="form-items-container">
            {item.status === 'Draft' && (
              <Alert className="mb-24" message={t('draftOrderReadOnlyInfo')} type="warning" showIcon />
            )}
            <ValidationError errors={this.state.validationErrors} />
            <ClientNotifications notifications={this.state.clientNotifications} />
            <Row>
              <Col xs={24} lg={6}>
                <Form.Item label={t('orderId')}>
                  <Input value={item.id} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} lg={6}>
                <Form.Item label={createLabel('createDate')}>
                  <Input value={formatUserTime(item.createDate, item.userName) || t('na')} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} lg={6}>
                <Form.Item label={createLabel('statusName')}>
                  <Input value={item.statusName} disabled />
                </Form.Item>
              </Col>
            </Row>
            <Tabs
              activeKey={this.state.tabsActiveKey}
              onChange={(tabsActiveKey) => this.setState({ tabsActiveKey })}
              defaultActiveKey="items"
            >
              <Tabs.TabPane key="order" tab={t('order')} forceRender>
                <Row>
                  <Col xs={24} lg={18}>
                    <Row>
                      <Col xs={24} lg={16}>
                        <Form.Item label={createLabel('billToAddressId')}>
                          {createFieldDecorator('billToAddressId')(
                            <Select
                              onChange={(value) => this.setItemValue('billToAddressId', value)}
                              placeholder={t('select')}
                              allowClear={false}
                              readOnly={!orderIsEditable}
                            >
                              {(this.state.billToAddressList ?? []).map((each) => (
                                <Option key={each.id} value={each.id}>
                                  <span {...getOptionProps(each)}>{each.displayName}</span>
                                </Option>
                              ))}
                            </Select>
                          )}
                        </Form.Item>
                        <Form.Item label={createLabel('shipToAddressId')}>
                          {createFieldDecorator('shipToAddressId')(
                            <Select
                              onChange={(value) => this.setItemValue('shipToAddressId', value)}
                              placeholder={t('select')}
                              allowClear={false}
                              readOnly={!orderIsEditable}
                            >
                              {(this.state.shipToAddressList ?? []).map((each) => (
                                <Option key={each.id} value={each.id}>
                                  <span {...getOptionProps(each)}>{each.displayName}</span>
                                </Option>
                              ))}
                            </Select>
                          )}
                        </Form.Item>
                        <Row>
                          <Col xs={24} lg={12}>
                            {requisitionerFields.map(({ key, type }) => (
                              <Form.Item
                                key={key}
                                label={
                                  key === 'requisitionerEmail' ? (
                                    <span>
                                      {createLabel(key)} <Help title={t('multipleEmailsHelp')} />
                                    </span>
                                  ) : (
                                    createLabel(key)
                                  )
                                }
                                required={this.props.customer.generalSettings.requireRequisitionerOnOrder}
                              >
                                {createFieldDecorator(
                                  key,
                                  key === 'requisitionerEmail' ? { extraRules: [validateMultipleEmails()] } : undefined
                                )(
                                  <Input
                                    type={type}
                                    onChange={(e) => this.setItemValue(key, e.target.value)}
                                    readOnly={!orderIsEditable}
                                  />
                                )}
                              </Form.Item>
                            ))}
                          </Col>
                          <Col xs={24} lg={12}>
                            <Form.Item label={createLabel('accountCode')}>
                              {createFieldDecorator('accountCode')(
                                <Input
                                  onChange={(e) => this.setItemValue('accountCode', e.target.value)}
                                  readOnly={!orderIsEditable}
                                />
                              )}
                            </Form.Item>
                            <Form.Item label={HARD_SPACE}>
                              {createFieldDecorator('notifyRequisitioner', { valuePropName: 'checked' })(
                                <Checkbox readOnly={!orderIsEditable}>
                                  {createLabel('notifyRequisitioner')}
                                  <Help title={t('notifyRequisitionerTooltip')} />
                                </Checkbox>
                              )}
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>
                      <Col xs={24} lg={8}>
                        <Form.Item
                          label={
                            <span>
                              {createLabel('approveDate')}{' '}
                              <Popover
                                title={t('approvalStatus')}
                                content={<span dangerouslySetInnerHTML={{ __html: item.orderApprovalInfo }} />}
                              >
                                <span
                                  className="align-middle"
                                  style={{
                                    visibility: isEmpty(item.orderApprovalInfo) ? 'hidden' : 'visible',
                                  }}
                                >
                                  <Icon type="Search" />
                                </span>
                              </Popover>
                            </span>
                          }
                        >
                          <Input
                            value={
                              isValidDate(item.approveDate)
                                ? formatUserTime(item.approveDate, item.approveUserName)
                                : !['Draft', 'New', 'PendingApproval'].includes(item.status) &&
                                    isValidDate(item.confirmDate)
                                  ? formatUserTime(item.confirmDate, item.userName)
                                  : ''
                            }
                            disabled
                          />
                        </Form.Item>
                        {['confirmDate', 'receivedDate'].map((each) => (
                          <Form.Item key={each} label={createLabel(each)}>
                            <Input value={formatUserTime(item[each], item.userName)} disabled />
                          </Form.Item>
                        ))}
                      </Col>
                    </Row>
                    <Comments
                      value={item.comment}
                      onChange={(value) => this.setItemValue('comment', value)}
                      rows={7}
                      label={createLabel('comment')}
                      title={t('addComment')}
                      readOnly={!orderIsEditable || this.props.tenantGroupIsCardinal}
                    />
                  </Col>
                  {allowPricing() && (
                    <Col xs={24} lg={6}>
                      {[
                        ...(this.props.customer.moduleSettings.enableTax
                          ? ['exemptTotal', 'taxableTotal', 'taxTotal']
                          : []),
                        'subTotal',
                        'adjustmentsTotal',
                        'freightTotal',
                        'orderTotal',
                      ].map((each) => (
                        <Form.Item
                          key={each}
                          label={
                            each === 'taxTotal' ? (
                              <span>
                                {createLabel(each)}{' '}
                                {`(${item.taxRate.toLocaleString(this.props.locale, {
                                  style: 'percent',
                                  maximumFractionDigits: 2,
                                })})`}
                              </span>
                            ) : (
                              createLabel(each)
                            )
                          }
                        >
                          <Input className="text-right" value={toLocaleCurrency(item[each])} disabled />
                        </Form.Item>
                      ))}
                    </Col>
                  )}
                </Row>
              </Tabs.TabPane>
              <Tabs.TabPane key="items" tab={t('itemsTab')} forceRender>
                <OrderItems
                  items={this.state.orderItems ?? []}
                  pageTotals={this.state.orderItemsTotals ?? {}}
                  onChange={this.handleOrderItemsChange}
                  settingsType={this.props.settingsType}
                  parentRecord={{
                    fieldSettings,
                    ...item,
                    ...pick(this.state, ['orderItems', 'orderSupplierItems', 'documentItems', 'purchaseOrderNumbers']),
                    orderIsReadOnly,
                    orderIsReceivable,
                    orderIsEditable,
                    orderIsPartiallyReceived,
                  }}
                  readOnly={!orderIsEditable && !orderIsReceivable}
                  onFilter={(orderItemsFilterDto) => this.setState({ orderItemsFilterDto })}
                  parentHasUnsavedChanges={this.hasUnsavedChanges()}
                  downloadItems={this.props.downloadOrderItems}
                  loading={this.state.orderItemsLoading}
                  onMoveOrderItems={() => this.fetchItem()}
                />
              </Tabs.TabPane>
              <Tabs.TabPane key="suppliers" tab={t('suppliers')} forceRender>
                <OrderSuppliers
                  items={this.state.orderSupplierItems ?? []}
                  pageTotals={this.state.orderSupplierItemsTotals ?? {}}
                  onChange={this.handleOrderSuppliersItemsChange}
                  settingsType={this.props.settingsType}
                  parentRecord={{
                    fieldSettings,
                    ...item,
                    ...pick(this.state, ['orderItems', 'orderSupplierItems', 'documentItems', 'purchaseOrderNumbers']),
                  }}
                  readOnly={!orderIsEditable}
                  parentHasUnsavedChanges={this.hasUnsavedChanges()}
                  targetTenantId={targetTenantId}
                />
              </Tabs.TabPane>
              <Tabs.TabPane key="documents" tab={t('documents')} forceRender>
                <Documents
                  domainObjectId={item.id}
                  domainObjectType="Order"
                  items={this.state.documentItems ?? []}
                  onChange={(values) => this.setState({ documentItems: values })}
                  readOnly={!orderIsEditable}
                  targetTenantId={targetTenantId}
                />
              </Tabs.TabPane>
              {!isNil(item.id) && (getUserPermission('Master') === 'Yes' || getUserPermission('Forms') !== 'None') && (
                <Tabs.TabPane key="forms" tab={t('forms')} forceRender>
                  <FormInstancesContext.Provider value={{ targetTenantId }}>
                    <FormInstances
                      settingsType="order"
                      objectType="Order"
                      objectId={item.id}
                      parentRecord={item}
                      items={this.state.formInstanceItems ?? []}
                      onChange={this.saveFormInstances}
                      readOnly={!orderIsEditable}
                    />
                  </FormInstancesContext.Provider>
                </Tabs.TabPane>
              )}
            </Tabs>
          </div>
          <div className="form-buttons-container">
            <div className="float-right text-right">
              <FormButtons
                readOnly={!orderIsEditable && !orderIsReceivable}
                onCancel={this.props.onCancel}
                onSubmit={this.handleSubmit}
                saveButtonLoading={this.state.saveButtonLoading}
                saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
                disabled={this.state.formButtonsDisabled}
              />
            </div>
            {!(this.props.tenantGroupIsCardinal && item.ackReceivedForOrder) && (
              <div className="float-left text-left">
                {!userIsSupplierOrDistributer && item.status === 'New' && item.userAuthorizedForAllLocations && (
                  <Button
                    type="primary"
                    onClick={() => this.invokeExtraAction('sendOrder')}
                    loading={this.state.sendOrderButtonLoading}
                    disabled={isEmpty(this.state.orderItems) || !orderIsEditable || extraButtonsDisabled}
                  >
                    {t('send')}
                  </Button>
                )}{' '}
                {!userIsSupplierOrDistributer && item.status === 'Sent' && item.userAuthorizedForAllLocations && (
                  <Button
                    type="primary"
                    onClick={() => this.invokeExtraAction('resendOrder')}
                    loading={this.state.resendOrderButtonLoading}
                    disabled={isEmpty(this.state.orderItems) || extraButtonsDisabled}
                  >
                    {t('resend')}
                  </Button>
                )}{' '}
                {!userIsSupplierOrDistributer &&
                  item.status === 'PendingApproval' &&
                  !item.userAuthorizedForOrderValue && (
                    <Button
                      type="primary"
                      onClick={() => this.invokeExtraAction('requestApproval')}
                      loading={this.state.requestApprovalButtonLoading}
                      disabled={isEmpty(this.state.orderItems) || !orderIsEditable || extraButtonsDisabled}
                    >
                      {t('requestApproval')}
                    </Button>
                  )}{' '}
                {!userIsSupplierOrDistributer &&
                  item.status === 'PendingApproval' &&
                  item.userAuthorizedForOrderValue &&
                  item.userAuthorizedForAllLocations && (
                    <Button
                      type="primary"
                      onClick={() => this.invokeExtraAction('approveOrder')}
                      loading={this.state.approveOrderButtonLoading}
                      disabled={isEmpty(this.state.orderItems) || !orderIsEditable || extraButtonsDisabled}
                    >
                      {t('approve')}
                    </Button>
                  )}{' '}
                {userIsSupplierOrDistributer &&
                  !this.props.tenantTypeIsTrms &&
                  ['Sent', 'Backorder'].includes(item.status) && (
                    <Button
                      type="primary"
                      onClick={() => this.invokeExtraAction('processOrder')}
                      loading={this.state.processOrderButtonLoading}
                      disabled={isEmpty(this.state.orderItems) || extraButtonsDisabled}
                    >
                      {t('process')}
                    </Button>
                  )}{' '}
                {userIsSupplierOrDistributer && !this.props.tenantTypeIsTrms && item.status === 'Processed' && (
                  <Tooltip title="">
                    <Button
                      type="primary"
                      onClick={() => this.invokeExtraAction('undoProcessed')}
                      loading={this.state.undoProcessedButtonLoading}
                      disabled={this.state.formButtonsDisabled}
                    >
                      {t('undoProcessed')}
                    </Button>
                  </Tooltip>
                )}
                {item.id &&
                  item.status === 'Draft' &&
                  !isEmpty(this.state.orderItems) &&
                  getUserPermission('Orders') === 'All' && (
                    <Button
                      type="primary"
                      onClick={this.handleSubmitOrderClick}
                      loading={this.state.actionButtonLoading}
                    >
                      {t('submit')}
                    </Button>
                  )}
              </div>
            )}
          </div>
        </Form>
        {orderIsEditable && getUserPermission('Purchase') !== 'No' && !orderIsPartiallyReceived && (
          <Drawer title={t('addToOrder')} visible sideDrawer>
            <div className="form-items-actions text-right inline-block">
              <BadgeContainer>
                <Badge {...(this.state.quickscan ? { count: '!' } : {})}>
                  <Button onClick={() => this.setState({ showDataEntrySettings: true })}>
                    <Icon type="Settings" />
                  </Button>
                </Badge>
              </BadgeContainer>
            </div>
            <SideForm
              quickscan={this.state.quickscan}
              requireQuantity={this.state.requireQuantity}
              onSubmitting={(pending) => this.setState({ formButtonsDisabled: pending, orderItemsLoading: pending })}
              onSubmit={this.handleSideFormSubmit}
              loading={this.state.sideFormLoading}
              parentRecord={{
                fieldSettings,
                ...item,
                ...pick(this.state, ['orderItems', 'orderSupplierItems', 'documentItems', 'purchaseOrderNumbers']),
              }}
              parentHasUnsavedChanges={this.hasUnsavedChanges()}
              onPunchout={() => this.setState({ tabsActiveKey: 'items' }, this.fetchItem)}
              onAddFromList={this.handleAddFromList}
              addFromListLoading={this.state.addFromListLoading}
              domainObjectType="Order"
              disabled={extraButtonsDisabled}
              quantityMin={quantityOrderedMin}
            />
          </Drawer>
        )}
        <Modal
          title={t('dataEntrySettings')}
          visible={this.state.showDataEntrySettings}
          onCancel={() => this.setState({ showDataEntrySettings: false })}
          footer={null}
        >
          <Form.Item>
            <Checkbox
              checked={this.state.quickscan}
              onChange={(e) => this.setState({ quickscan: e.target.checked }, this.saveState)}
            >
              {t('quickscan')} <Help title={t('quickScanInfo')} />
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Checkbox
              checked={this.state.requireQuantity}
              onChange={(e) => this.setState({ requireQuantity: e.target.checked }, this.saveState)}
            >
              {t('requireQuantity')}
            </Checkbox>
          </Form.Item>
        </Modal>
        <PackingListNumber
          visible={this.state.showAddPackingListNumberModal}
          onCancel={() => this.setState({ showAddPackingListNumberModal: false })}
          onSubmit={this.handleAddPackingListNumberSubmit}
        />
        <SendEmail
          visible={this.state.sendEmailVisible}
          loading={this.state.sendEmailLoading}
          onSubmit={this.handleSendEmailSubmit}
          onCancel={() => this.setState({ sendEmailVisible: false })}
        />
        <Modal
          title={t('printPreview')}
          okText={t('print')}
          onOk={createPrintHandler(this.printableRef)}
          onCancel={() => this.setState({ printVisible: false }, resetPrintHandler)}
          width={992}
          visible={this.state.printVisible}
        >
          <div ref={this.printableRef} className="tofino-print-preview">
            <Print
              parentRecord={{
                ...this.state.printItem,
                orderItems,
                orderSupplierItems,
              }}
            />
          </div>
        </Modal>
        <Modal
          title={t('requisitionerInformation')}
          okText={t('saveAndSubmit')}
          onOk={this.handleSaveAndSubmitOrderClick}
          okButtonProps={{
            disabled: requisitionerFields.some(
              ({ key }) => isEmpty(item[key]) || (key === 'requisitionerEmail' && !areEmails(item[key]))
            ),
            loading: this.state.actionButtonLoading,
          }}
          onCancel={() => this.setState({ showRequisitionerInformationModal: false })}
          visible={this.state.showRequisitionerInformationModal}
        >
          <Alert message={t('errorNoRequisitioner')} type="warning" showIcon />
          {requisitionerFields.map(({ key, type }) => (
            <Form.Item
              key={key}
              label={createLabel(key)}
              colon={false}
              validateStatus={
                isEmpty(item[key]) || (key === 'requisitionerEmail' && !areEmails(item[key])) ? 'error' : undefined
              }
              help={
                isEmpty(item[key])
                  ? t('requiredField')
                  : key === 'requisitionerEmail' && !areEmails(item[key])
                    ? t('errorInvalidEmailAddress')
                    : undefined
              }
              required
            >
              <Input
                type={type}
                value={item[key]}
                onChange={(e) =>
                  this.setState(
                    produce((draft) => {
                      draft.item[key] = e.target.value
                    })
                  )
                }
              />
            </Form.Item>
          ))}
        </Modal>
      </>
    )
  }
}

export default flow(
  withRouter,
  Form.create({
    onValuesChange: (...params) => Emitter.emit(FORM_CHANGE_EVENT, ...params),
  })
)(FormView)
